/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Fragment, useEffect, useState } from 'react';
import { Collapse } from '@blueprintjs/core';
import { useFacetsState, FACET_STATE } from 'hooks/useFacetsState';
import { FacetItem } from '../../../components/facets/facetItem';
import { useIsExpanded } from 'views/filterHub/hierarchicalFacet/expandedStateManager';

/**
 * HierarchicalFacet displays a single hierarchical facet
 * Determines whether to render a tree node or a leaf node.
 *
 * @param {any} facet
 * @param {DILProperty} property indicates the specific property to render
 * @param {string} parentKey path prefix for parent facet
 * @param {boolean} isAncestorSelected
 * @param {boolean} isAncestorExcluded
 * @param {number} childLimit indicates number of children to display - if not provided, display all
 */
export const HierarchicalFacetDetail = ({
	facet,
	property,
	parentKey = null,
	isAncestorSelected = false,
	isAncestorExcluded = false,
	childLimit,
	depth = 0,
	...props
}) => {
	const {
		toggleHierarchicalFacet,
		getHierarchicalFacetState,
		toggleExcludeHierarchicalFacet,
		hasSelectedChild,
		hasExcludedChild,
		getTextFilter,
	} = useFacetsState();
	const textFilter = getTextFilter();

	const { key: name, count, children, reference } = facet;
	const propertyName = property.name;
	const startsWithKey = parentKey
		? `${parentKey}${name}${children ? property.delimiter : ''}`
		: `${name}${property.delimiter}`;

	const [isOpen, toggleOpen] = useIsExpanded(name);

	const state = getHierarchicalFacetState(property.name, startsWithKey);
	const isSelected = state === FACET_STATE.ON;
	const isExcluded = state === FACET_STATE.OFF;
	const preventToggle = (isAncestorSelected || isAncestorExcluded) && !isExcluded;

	//if not checked/excluded, look for children that could be checked/excluded (for indeterminate state)
	const containsSelectedChild =
		!isAncestorSelected &&
		!isAncestorExcluded &&
		state === FACET_STATE.UNSELECTED &&
		hasSelectedChild(propertyName, name);
	const containsExcludedChild =
		!isAncestorSelected &&
		!isAncestorExcluded &&
		state === FACET_STATE.UNSELECTED &&
		hasExcludedChild(propertyName, name);

	const toggleSelected = () => {
		if (!preventToggle) toggleHierarchicalFacet(propertyName, startsWithKey, isAncestorSelected);
	};
	const toggleExcluded = () => {
		toggleExcludeHierarchicalFacet(property.name, startsWithKey);
	};

	return (
		<Fragment>
			<FacetItem
				facetCheckboxProps={{
					isSelected,
					isExcluded,
					isAncestorSelected,
					isAncestorExcluded,
					hasSelectedChild: containsSelectedChild,
					hasExcludedChild: containsExcludedChild,
					disabled: preventToggle,
				}}
				facetCountProps={{
					set: count,
					total: reference,
				}}
				facetName={name}
				facetTextFilter={textFilter}
				onClickSelect={toggleSelected}
				onClickExclude={toggleExcluded}
				onClickOpen={toggleOpen}
				isOpen={isOpen}
				isBottom={children == null}
				depth={depth}
				{...props}
			/>

			{children !== undefined && (
				<Collapse isOpen={isOpen} keepChildrenMounted={false}>
					{children.slice(0, childLimit || children.length).map((entry) => (
						<HierarchicalFacetDetail
							depth={depth + 1} // recursive depth determines padding
							key={`${startsWithKey}${entry.key}`}
							facet={entry}
							property={property}
							parentKey={startsWithKey}
							isAncestorSelected={state === FACET_STATE.ON || isAncestorSelected}
							isAncestorExcluded={state === FACET_STATE.OFF || isAncestorExcluded}
							childLimit={childLimit}
						/>
					))}
				</Collapse>
			)}
		</Fragment>
	);
};
